import { FC, useContext, useMemo } from "react";
import { Box, Flex } from "@otrium/core";
import { Trans } from "@lingui/macro";
import {
  Term,
  StyledText,
  Title,
  NoResultsContainer,
} from "./NoSearchResults.styled";
import { CheckIcon } from "src/icons/CheckIcon";
import { Carousel } from "src/organisms/Carousel";
import { useQueryRecommendations } from "src/hooks/search/use-query-recommendations";
import { SearchResultsDispatchContext } from "src/organisms/SearchResults/search-results.context";
import { productSuggestionToCatalogProduct } from "src/organisms/SearchResults/utils";
import { ProductCardWishlist } from "src/organisms/ProductCardWishlist";
import { ResultHeader } from "src/organisms/SearchResults/components/ResultHeader";
import { ProductSuggestionsWrapper } from "src/organisms/SearchResults/SearchResults.styled";
import { PRODUCT_CAROUSEL_SLIDES } from "src/utils/constants";

interface Props {
  term: string;
}

const NoSearchResults: FC<Props> = ({ term }) => {
  const { results } = useQueryRecommendations();

  const { onSuggestionClick } = useContext(SearchResultsDispatchContext);

  const productItems = useMemo(() => {
    return (
      results?.products?.map((product, idx) => (
        <ProductCardWishlist
          zoomImageOnHover={true}
          showHoverBorder={true}
          key={product.id}
          product={productSuggestionToCatalogProduct(product)}
          position={idx}
          onSearchProductClick={() =>
            onSuggestionClick({
              gateLevel: product.gateLevel,
              itemId: product.id,
              position: idx,
              type: product.type,
            })
          }
        />
      )) || []
    );
  }, [results?.products, onSuggestionClick]);

  return (
    <Box data-testid="search-no-results" bg="tone.sand">
      <NoResultsContainer overflow="auto">
        <Flex
          flexDirection={["column", "column", "column", "row"]}
          flexWrap="wrap"
        >
          <Title as="h4">
            <Trans>No results for</Trans>
          </Title>
          <Term as="h4" ml={[0, 0, 0, 2]} mt={[2, 2, 2, 0]}>
            ‘{term}’
          </Term>
        </Flex>
        <StyledText fontWeight={600} my={3}>
          <Trans>Try again with a different search term.</Trans>
        </StyledText>
        <Flex alignItems="center">
          <CheckIcon />
          <StyledText ml={2}>
            <Trans>Check the spelling of your search term</Trans>
          </StyledText>
        </Flex>
        <Flex mt={2} alignItems="center">
          <CheckIcon />
          <StyledText ml={2}>
            <Trans>Make your search term less specific</Trans>
          </StyledText>
        </Flex>
        {productItems.length ? (
          <ProductSuggestionsWrapper className="no-results-suggestions">
            <ResultHeader title={<Trans>You may also like</Trans>} />
            <Carousel
              items={productItems}
              itemsPerView={PRODUCT_CAROUSEL_SLIDES}
            />
          </ProductSuggestionsWrapper>
        ) : null}
      </NoResultsContainer>
    </Box>
  );
};

export default NoSearchResults;
